import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { conversationsApi, InternalNote } from '@/services/conversationsApi'
import { useAuthStore } from '@/store/authStore'
import {
  StickyNote,
  Plus,
  Edit2,
  Trash2,
  X,
  Check,
  Clock,
  AlertCircle,
  Bot,
} from 'lucide-react'
import { Button } from '@/components/ui/button'

interface InternalNotesProps {
  conversationId: string
}

export function InternalNotes({ conversationId }: InternalNotesProps) {
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  const [newNoteContent, setNewNoteContent] = useState('')
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null)
  const [editingContent, setEditingContent] = useState('')
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)

  // Query for notes
  const { data: notes = [], isLoading, error } = useQuery({
    queryKey: ['notes', conversationId],
    queryFn: () => conversationsApi.getNotes(conversationId),
    enabled: !!conversationId,
  })

  // Mutation for creating notes
  const createNoteMutation = useMutation({
    mutationFn: (content: string) => conversationsApi.createNote(conversationId, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notes', conversationId] })
      setNewNoteContent('')
    },
  })

  // Mutation for updating notes
  const updateNoteMutation = useMutation({
    mutationFn: ({ noteId, content }: { noteId: string; content: string }) =>
      conversationsApi.updateNote(conversationId, noteId, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notes', conversationId] })
      setEditingNoteId(null)
      setEditingContent('')
    },
  })

  // Mutation for deleting notes
  const deleteNoteMutation = useMutation({
    mutationFn: (noteId: string) => conversationsApi.deleteNote(conversationId, noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notes', conversationId] })
      setDeleteConfirmId(null)
    },
  })

  const handleCreateNote = () => {
    if (newNoteContent.trim() && newNoteContent.length <= 2000) {
      createNoteMutation.mutate(newNoteContent.trim())
    }
  }

  const handleStartEdit = (note: InternalNote) => {
    setEditingNoteId(note.id)
    setEditingContent(note.content)
  }

  const handleSaveEdit = () => {
    if (editingContent.trim() && editingContent.length <= 2000 && editingNoteId) {
      updateNoteMutation.mutate({ noteId: editingNoteId, content: editingContent.trim() })
    }
  }

  const handleCancelEdit = () => {
    setEditingNoteId(null)
    setEditingContent('')
  }

  const handleDeleteConfirm = (noteId: string) => {
    deleteNoteMutation.mutate(noteId)
  }

  const formatDate = (date: Date) => {
    const d = new Date(date)
    return d.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const isBotNote = (note: InternalNote) => note.source === 'bot_summary'

  const getAuthorInitials = (note: InternalNote) => {
    if (isBotNote(note)) return 'IA'
    if (note.user?.name) {
      return note.user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    }
    return note.user?.email?.slice(0, 2).toUpperCase() || '??'
  }

  const getAuthorColor = (note: InternalNote) => {
    if (isBotNote(note)) return 'bg-purple-600'
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-red-500',
      'bg-yellow-500',
      'bg-teal-500',
    ]
    const index = (note.userId || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
    return colors[index % colors.length]
  }

  const isOwnNote = (note: InternalNote) => {
    return user?.id === note.userId
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="w-6 h-6 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8 text-red-500">
        <AlertCircle className="h-5 w-5 mr-2" />
        <span>Error al cargar las notas</span>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-3 flex items-center gap-2">
        <StickyNote className="h-5 w-5 text-yellow-600" />
        <h3 className="font-semibold text-yellow-900">Notas Internas</h3>
        <span className="ml-auto text-xs text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded-full">
          Solo para agentes
        </span>
      </div>

      {/* Notes List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {notes.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <StickyNote className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>No hay notas aún</p>
            <p className="text-sm">Agrega la primera nota usando el formulario de abajo</p>
          </div>
        ) : (
          notes.map((note) => (
            <div
              key={note.id}
              className={`rounded-lg border p-3 shadow-sm ${isBotNote(note) ? 'bg-purple-50 border-purple-200' : 'bg-white border-gray-200'}`}
            >
              {/* Note Header */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full ${getAuthorColor(note)} flex items-center justify-center text-white text-xs font-medium`}>
                    {isBotNote(note) ? <Bot className="h-4 w-4" /> : getAuthorInitials(note)}
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-medium text-gray-900">
                        {isBotNote(note) ? 'Agente IA' : (note.user?.name || note.user?.email || 'Usuario')}
                      </p>
                      {isBotNote(note) && (
                        <span className="inline-flex items-center gap-0.5 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-medium">
                          <Bot className="h-3 w-3" />
                          Resumen IA
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDate(note.createdAt)}
                    </p>
                  </div>
                </div>

                {/* Actions — hidden for bot notes */}
                {!isBotNote(note) && isOwnNote(note) && editingNoteId !== note.id && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleStartEdit(note)}
                      className="p-1.5 hover:bg-gray-100 rounded-full transition-colors text-gray-500 hover:text-blue-600"
                      title="Editar"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setDeleteConfirmId(note.id)}
                      className="p-1.5 hover:bg-gray-100 rounded-full transition-colors text-gray-500 hover:text-red-600"
                      title="Eliminar"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Note Content */}
              {!isBotNote(note) && editingNoteId === note.id ? (
                <div className="space-y-2">
                  <textarea
                    value={editingContent}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditingContent(e.target.value)}
                    placeholder="Contenido de la nota..."
                    className="w-full min-h-[80px] px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    maxLength={2000}
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">
                      {editingContent.length}/2000
                    </span>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleCancelEdit}
                      >
                        <X className="h-4 w-4 mr-1" />
                        Cancelar
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSaveEdit}
                        disabled={!editingContent.trim() || updateNoteMutation.isPending}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Guardar
                      </Button>
                    </div>
                  </div>
                </div>
              ) : deleteConfirmId === note.id ? (
                <div className="bg-red-50 rounded-lg p-3">
                  <p className="text-sm text-red-700 mb-2">
                    ¿Estás seguro de que quieres eliminar esta nota?
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setDeleteConfirmId(null)}
                    >
                      Cancelar
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDeleteConfirm(note.id)}
                      disabled={deleteNoteMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Eliminar
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{note.content}</p>
              )}
            </div>
          ))
        )}
      </div>

      {/* New Note Form */}
      <div className="border-t border-gray-200 bg-white p-4">
        <div className="space-y-2">
          <textarea
            value={newNoteContent}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNewNoteContent(e.target.value)}
            placeholder="Escribe una nota interna..."
            className="w-full min-h-[80px] px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 resize-none"
            maxLength={2000}
            disabled={createNoteMutation.isPending}
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400">
              {newNoteContent.length}/2000
            </span>
            <Button
              onClick={handleCreateNote}
              disabled={!newNoteContent.trim() || createNoteMutation.isPending}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              <Plus className="h-4 w-4 mr-1" />
              Agregar Nota
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
